import React, { useState } from "react"
import PropTypes from "prop-types"
import { isEmpty } from "lodash"
import { reduxForm, propTypes } from "redux-form"
import { injectIntl, defineMessages } from "react-intl"
import { useDispatch, useSelector } from "react-redux"

import Box from "@material-ui/core/Box"
import FormGroup from "@material-ui/core/FormGroup"
import {
  Button,
  Grid,
  Paper,
  Step,
  Stepper,
  StepButton,
  Typography
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import AutorenewIcon from "@material-ui/icons/Autorenew"
import SaveIcon from "@material-ui/icons/Save"

import fieldsValidator from "./fieldsValidator"
import FormController from "./formController"
import RequiredStar from "./RequiredStar"

import { ButtonClear, ButtonSubmit } from "../../component/buttons"
import {
  containedPrimary,
  outlinedPrimary
} from "../../component/buttons/ButtonPropTypes"
import TooltipIconButton from "../../component/buttons/TooltipIconButton"
import { actions as dialog } from "../../component/dialog"
import useConfirmDialog from "../../component/dialog/useConfirmDialog"
import useAppStyle from "../../styles/appStyle"
import { words } from "../../i18n/lstrings"
import useSmartFormat from "../../i18n/useSmartFormat"
import UserFormRow from "./UserFormRow"

export const CustomerUserFormName = "CustomUserForm"

const messages = defineMessages({
  mandatoryField: {
    id: "form.mandatory.field",
    defaultMessage: "This field is mandatory"
  },
  currentModificationWillBeLost: {
    id: "form.modification.lost",
    defaultMessage:
      "Current modifications will be lost. Do you really want to clean current values?"
  },
  requiredFieldDescription: {
    id: "form.mandatory.field.description",
    defaultMessage: "indicates a required field",
    description: "Label written right after the * in the bottom of the form"
  },
  disabledDocuSign: {
    id: "form.disabled.config.docuSign",
    defaultMessage:
      "The electronic signature service is deactivated. Your form cannot currently be submitted. Please contact the administrator.",
    description: "message displays if docuSign is disabled"
  }
})

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%"
  },
  formGroup: {
    maxWidth: "inherit"
  },
  gridContainer: {
    top: 70,
    left: 0,
    right: 0
  },
  paper: {
    padding: theme.spacing(2)
  },
  actions: {
    margin: theme.spacing(2)
  }
}))

function CustomerUserFormRows(props) {
  const { controller, isPaginated, activeStep, onBlur } = props
  const formRows = isPaginated
    ? controller.rowsOfPage(activeStep)
    : controller.rows()
  const elementsNumbers = controller.elementsNumbers()

  return formRows.map((row, idx) => (
    <UserFormRow
      key={row.join(",")}
      controller={controller}
      rowIndex={idx}
      rowElements={controller.elementsOfRow(row)}
      onBlur={onBlur}
      elementsNumbers={elementsNumbers}
    />
  ))
}

function CustomUserForm(props) {
  // reduxForm props
  const {
    handleSubmit,
    pristine,
    submitting,
    invalid: invalidProp,
    reset,
    initialize,
    validate,
    ...rest
  } = props
  const { controller, goBack, save, onSubmit, disableSubmit } = rest

  const format = useSmartFormat()
  const [shouldAutoSave, setShouldAutoSave] = useState(false)
  // TODO : L'auto save ne marche pas ou bug pour certains éléments (Matrix de sélection)
  const [activeStep, setActiveStep] = useState(0)
  const classes = useStyles()
  const appClasses = useAppStyle()
  const dispatch = useDispatch()
  const locale = useSelector((state) => state.intl.locale)
  const isSubmitting = useSelector((state) => state.ui.isLinearProgressEnabled)
  const localValues = useSelector(
    (state) => state.form[CustomerUserFormName]?.values
  )
  const docusignDisabled = useSelector((state) => state.esign.disabled)
  const { signable } = controller.form
  const formPages = controller.pages()
  const isPaginated = formPages.length > 0
  const isFirstStep = isPaginated && activeStep === 0
  const isLastStep =
    !isPaginated || (isPaginated && activeStep === formPages.length - 1)
  const isStrict = controller.strict()

  const localValidate = validate(localValues, props)
  const invalid = invalidProp || !isEmpty(localValidate) // We now must process invalidation manually because pagination doesn't change 'values' in props

  const saveIconProps = pristine ? { color: "primary" } : { color: "error" }

  useConfirmDialog("resetCustomUserForm", () => reset())

  const handleSaveForm = handleSubmit((formValues) => {
    // When the new form is saved to the DB, tell redux-forms that this is the new initial form
    save(formValues).then(() => initialize(formValues))
  })

  const showWarningBeforeResetForm = () => {
    dispatch(
      dialog.show(messages.currentModificationWillBeLost, {
        toConfirm: { resetCustomUserForm: true }
      })
    )
  }

  const handleStepClick = (index) => {
    handleSaveForm()
    setActiveStep(index)
  }

  const handleBackClick = () => {
    setActiveStep(Math.max(activeStep - 1, 0))
  }

  const handleNextClick = () => {
    handleSaveForm()
    setActiveStep(Math.min(activeStep + 1, formPages.length - 1))
  }

  const validateCurrentPage = () => {
    if (!isPaginated || !isStrict) {
      return null
    }
    const pageElements = controller.elementsOfPage(activeStep)
    const pageValues = pageElements.reduce((acc, { id }) => {
      acc[id] = localValues[id]
      return acc
    }, {})
    return validate(pageValues, props, pageElements)
  }

  const currentPageIsValid = isEmpty(validateCurrentPage())

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.container}>
      <Grid container direction="row" className={classes.gridContainer}>
        <Grid item xs={1} className={classes.actions}>
          <Grid item>
            <TooltipIconButton
              onClick={goBack}
              label={words.return}
              disabled={isSubmitting}
            >
              <ArrowBackIcon />
            </TooltipIconButton>
          </Grid>
          <Grid item>
            <TooltipIconButton
              onClick={handleSaveForm}
              label={words.save}
              disabled={isSubmitting}
            >
              <SaveIcon {...saveIconProps} />
            </TooltipIconButton>
          </Grid>
          <Grid item>
            <TooltipIconButton
              onClick={() => setShouldAutoSave(!shouldAutoSave)}
              label={words.autosave}
              color={shouldAutoSave ? "secondary" : "disabled"}
            >
              <AutorenewIcon />
            </TooltipIconButton>
          </Grid>
        </Grid>
        <Grid item xs>
          <Box m={2}>
            <Typography variant="h4">{controller.name()}</Typography>
          </Box>
          {isPaginated && (
            <Stepper activeStep={activeStep} nonLinear alternativeLabel>
              {formPages.map(({ title }, index) => (
                <Step key={title[locale]}>
                  <StepButton
                    disabled={
                      (!currentPageIsValid && index > activeStep) ||
                      (isStrict && index > activeStep + 1)
                    }
                    onClick={() => handleStepClick(index)}
                  >
                    {title[locale]}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}
          <Paper className={classes.paper}>
            <FormGroup className={classes.formGroup} />
            <CustomerUserFormRows
              controller={controller}
              onBlur={shouldAutoSave ? handleSaveForm : undefined}
              isPaginated={isPaginated}
              activeStep={activeStep}
            />
            <Typography style={{ paddingTop: 16, paddingLeft: 8 }}>
              <RequiredStar /> {format(messages.requiredFieldDescription)}
            </Typography>
            <Box className={appClasses.formButton}>
              <ButtonClear
                disabled={pristine || submitting}
                onClick={showWarningBeforeResetForm}
              />
              {isPaginated && !isFirstStep && (
                <Button {...outlinedPrimary} onClick={handleBackClick}>
                  {format(words.back)}
                </Button>
              )}
              {isPaginated && !isLastStep && (
                <Button
                  {...containedPrimary}
                  onClick={handleNextClick}
                  disabled={!currentPageIsValid}
                >
                  {format(words.next)}
                </Button>
              )}
              {isLastStep && (
                <ButtonSubmit
                  disabled={
                    disableSubmit ||
                    (signable && !docusignDisabled) ||
                    invalid ||
                    submitting
                  }
                />
              )}
            </Box>
            {signable && !docusignDisabled && (
              <Typography style={{ paddingTop: 16, paddingLeft: 8 }}>
                {format(messages.disabledDocuSign)}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
}

CustomUserForm.propTypes = {
  ...propTypes,
  controller: PropTypes.instanceOf(FormController).isRequired,
  goBack: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired
}

const validate = (values, props, elements) => {
  const { controller, intl } = props
  const { formatMessage: format } = intl

  const errors = {}
  const formElements = elements || controller.elements() // Passed "elements" parameter is used to validate only the current form page instead of validate the whole form directly
  formElements.forEach((el) => {
    const formValue = values[el.id]
    if (el.required && !formValue) {
      errors[el.id] = format(messages.mandatoryField)
    }

    if (formValue) {
      const validator = fieldsValidator[el.type]
      if (!validator) {
        return
      }
      const error = validator(el, formValue, errors, intl)
      if (error) {
        errors[el.id] = error
      }
    }
  })

  return errors
}

export default injectIntl(
  reduxForm({
    form: CustomerUserFormName,
    validate
  })(CustomUserForm)
)
